import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateMenuNodePayload,
  MenuNode,
  MenuReorderItem,
  MenuSectionReorderItem,
  MoveMenuNodePayload,
  UpdateMenuNodePayload,
} from '@/client/types/menus';
import { menuKeys, unwrapEnvelope } from './menus.query';

const MENUS = '/api/admin/menus';
const TIMEOUT = { timeout: 30_000 };

const invalidateMenus = (qc: ReturnType<typeof useQueryClient>, menid?: number) => {
  qc.invalidateQueries({ queryKey: menuKeys.all });
  if (menid != null) {
    qc.invalidateQueries({ queryKey: menuKeys.detail(menid) });
  }
};

const unwrapNode = (raw: any): MenuNode => unwrapEnvelope(raw) as MenuNode;

// ─── System A ─────────────────────────────────────────────────────────────────

export const useCreateMenuNodeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: CreateMenuNodePayload) => {
    const raw = await request<any>(MENUS, 'POST', payload, TIMEOUT);
    return unwrapNode(raw);
  }, 'Failed to create menu node');
  return useMutation({
    mutationFn: (payload: CreateMenuNodePayload) => fn(payload),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useUpdateMenuNodeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: UpdateMenuNodePayload) => {
    const { menid, ...body } = payload;
    const raw = await request<any>(`${MENUS}/${menid}`, 'PUT', body, TIMEOUT);
    return unwrapNode(raw);
  }, 'Failed to update menu node');
  return useMutation({
    mutationFn: (payload: UpdateMenuNodePayload) => fn(payload),
    onSuccess: (_d, { menid }) => invalidateMenus(qc, menid),
  });
};

export const useDeleteMenuNodeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { menid: number; hard?: boolean }) => {
    return request<any>(
      `${MENUS}/${args.menid}?hard=${!!args.hard}`,
      'DELETE',
      {},
      TIMEOUT,
    );
  }, 'Failed to delete menu node');
  return useMutation({
    mutationFn: (args: { menid: number; hard?: boolean }) => fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useReorderMenusMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (order: MenuReorderItem[]) => {
    const raw = await request<any>(`${MENUS}/reorder`, 'PUT', { order }, TIMEOUT);
    return unwrapEnvelope(raw);
  }, 'Failed to reorder menus');
  return useMutation({
    mutationFn: (order: MenuReorderItem[]) => fn(order),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useMoveMenuNodeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (payload: MoveMenuNodePayload) => {
    const { menid, ...body } = payload;
    const raw = await request<any>(`${MENUS}/${menid}/move`, 'POST', body, TIMEOUT);
    return unwrapNode(raw);
  }, 'Failed to move menu node');
  return useMutation({
    mutationFn: (payload: MoveMenuNodePayload) => fn(payload),
    onSuccess: (_d, { menid }) => invalidateMenus(qc, menid),
  });
};

// ─── System B ─────────────────────────────────────────────────────────────────

export const useAddMenuSectionMemberMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { section: string; etid: number; menu_sec_menid?: number | null }) => {
      const body =
        args.menu_sec_menid != null ? { menu_sec_menid: args.menu_sec_menid } : {};
      const raw = await request<any>(
        `${MENUS}/mid/${args.section}/${args.etid}`,
        'POST',
        body,
        TIMEOUT,
      );
      return unwrapEnvelope(raw);
    },
    'Failed to add section member',
  );
  return useMutation({
    mutationFn: (args: { section: string; etid: number; menu_sec_menid?: number | null }) =>
      fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useRemoveMenuSectionMemberMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { section: string; etid: number }) => {
    const raw = await request<any>(
      `${MENUS}/mid/${args.section}/${args.etid}`,
      'DELETE',
      {},
      TIMEOUT,
    );
    return unwrapEnvelope(raw);
  }, 'Failed to remove section member');
  return useMutation({
    mutationFn: (args: { section: string; etid: number }) => fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useReorderMenuSectionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { section: string; order: MenuSectionReorderItem[] }) => {
      const raw = await request<any>(
        `${MENUS}/mid/${args.section}/reorder`,
        'PUT',
        { order: args.order },
        TIMEOUT,
      );
      return unwrapEnvelope(raw) as { updated?: unknown[]; warning?: string; section?: string };
    },
    'Failed to reorder section',
  );
  return useMutation({
    mutationFn: (args: { section: string; order: MenuSectionReorderItem[] }) => fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};

// ─── Secondary category bar (per pillar) ──────────────────────────────────────

export const useAddSecBarMemberMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { pillar: string; etid: number; body?: Record<string, unknown> }) => {
      const raw = await request<any>(
        `${MENUS}/sec/${args.pillar}/${args.etid}`,
        'POST',
        args.body ?? {},
        TIMEOUT,
      );
      return unwrapEnvelope(raw);
    },
    'Failed to show category in bar',
  );
  return useMutation({
    mutationFn: (args: { pillar: string; etid: number; body?: Record<string, unknown> }) =>
      fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useRemoveSecBarMemberMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(async (args: { pillar: string; etid: number }) => {
    const raw = await request<any>(
      `${MENUS}/sec/${args.pillar}/${args.etid}`,
      'DELETE',
      {},
      TIMEOUT,
    );
    return unwrapEnvelope(raw);
  }, 'Failed to hide category from bar');
  return useMutation({
    mutationFn: (args: { pillar: string; etid: number }) => fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};

export const useReorderSecBarMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { pillar: string; order: MenuSectionReorderItem[] }) => {
      const raw = await request<any>(
        `${MENUS}/sec/${args.pillar}/reorder`,
        'PUT',
        { order: args.order },
        TIMEOUT,
      );
      return unwrapEnvelope(raw) as {
        updated?: unknown[];
        warning?: string;
        pillar?: string;
        etid_root?: number;
      };
    },
    'Failed to reorder secondary bar',
  );
  return useMutation({
    mutationFn: (args: { pillar: string; order: MenuSectionReorderItem[] }) => fn(args),
    onSuccess: () => invalidateMenus(qc),
  });
};
